import { Calendar } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { ProcedureAction } from "@/types/domain";
import { SectionTitle } from "./detail-parts";
import { NewBadge } from "./pills";

// Misma ventana que el badge "Nueva" de las licitaciones (NEW_CONTRACTING_WINDOW_MS).
const NEW_ACTION_WINDOW_MS = 48 * 60 * 60 * 1000;

const isNewAction = (action: ProcedureAction) =>
  Date.now() - new Date(action.publishedAt).getTime() < NEW_ACTION_WINDOW_MS;

export type ScheduleStage = {
  etapa: string;
  fechaInicio: string;
  fechaFin?: string;
  isActive: boolean;
  /** Ninguna etapa está en curso y esta es la última que empezó. */
  isLastActivity: boolean;
};

/**
 * Cronograma y acciones del procedimiento en una sola tarjeta: una tabla visible
 * a la vez, para que dos tablas seguidas no empujen los documentos fuera de vista.
 */
export function ScheduleActionsCard({
  schedule,
  actions,
  scheduleUpdatedAt,
}: {
  schedule: ScheduleStage[];
  actions: ProcedureAction[];
  /** Cuándo detectamos el último cambio del cronograma. */
  scheduleUpdatedAt: string | null;
}) {
  if (schedule.length === 0 && actions.length === 0) return null;

  const hasNewActions = actions.some(isNewAction);

  return (
    <Tabs
      defaultValue={schedule.length > 0 ? "cronograma" : "acciones"}
      className="gap-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex flex-col gap-0.5">
          <SectionTitle icon={<Calendar className="size-4 text-slate-400" />}>
            Cronograma y acciones
          </SectionTitle>
          {scheduleUpdatedAt && (
            <span className="pl-[22px] text-[11px] font-medium text-slate-400">
              Actualizado {formatDate(scheduleUpdatedAt)}
            </span>
          )}
        </div>
        <TabsList>
          <TabsTrigger
            value="cronograma"
            disabled={schedule.length === 0}
            className="cursor-pointer px-3 text-xs"
          >
            Cronograma
          </TabsTrigger>
          <TabsTrigger
            value="acciones"
            disabled={actions.length === 0}
            className="cursor-pointer px-3 text-xs"
          >
            Acciones{actions.length > 0 && ` · ${actions.length}`}
            {hasNewActions && (
              <span
                title="Hay acciones nuevas"
                className="size-1.5 rounded-full bg-violet-500"
              />
            )}
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="cronograma">
        <div className="max-h-50 overflow-y-auto rounded-2xl border bg-card">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b bg-slate-50 font-bold text-slate-500 dark:bg-slate-800/40">
                <th className="p-3">Actividad / Etapa</th>
                <th className="w-36 p-3">Inicio</th>
                <th className="w-36 p-3">Fin</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((stage, i) => (
                <tr
                  key={i}
                  className={cn(
                    "border-b last:border-0",
                    stage.isActive && "bg-primary/5 font-bold text-primary",
                    stage.isLastActivity &&
                      "bg-amber-50/70 dark:bg-amber-900/10",
                  )}
                >
                  <td className="p-3">
                    {stage.etapa}
                    {stage.isLastActivity && (
                      <span className="mt-1 block text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                        Última actividad
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-slate-500">{stage.fechaInicio}</td>
                  <td className="p-3 text-slate-500">
                    {stage.fechaFin || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TabsContent>

      <TabsContent value="acciones">
        <div className="max-h-50 overflow-y-auto rounded-2xl border bg-card">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b bg-slate-50 font-bold text-slate-500 dark:bg-slate-800/40">
                <th className="p-3">Acción</th>
                <th className="p-3">Motivo</th>
                <th className="w-36 p-3">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((action, i) => (
                <tr
                  key={`${action.action}-${action.publishedAt}-${i}`}
                  className="border-b last:border-0"
                >
                  <td className="p-3 font-medium">
                    <span className="flex flex-wrap items-center gap-1.5">
                      {action.action}
                      {isNewAction(action) && <NewBadge />}
                    </span>
                  </td>
                  <td className="p-3 text-slate-500">{action.reason || "-"}</td>
                  <td className="p-3 text-slate-500">
                    {formatDateTime(action.publishedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TabsContent>
    </Tabs>
  );
}
